/**
 * Geometry Validation Utility
 *
 * Validates GeoJSON geometries for topological correctness including:
 * - Self-intersection detection for polygons and linestrings
 * - Valid ring closure for polygons
 * - Minimum vertex count enforcement
 *
 * **Validates: Requirements 16.6, 17.5**
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A 2D coordinate pair [x, y]. */
type Coordinate = [number, number];

/** Result of geometry validation. */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/** Supported GeoJSON geometry for validation. */
export interface ValidatableGeometry {
  type: string;
  coordinates: unknown;
}

// ---------------------------------------------------------------------------
// Segment intersection detection
// ---------------------------------------------------------------------------

/**
 * Determines if two line segments (p1-p2) and (p3-p4) intersect.
 * Uses the cross-product orientation method.
 *
 * Returns true if the segments properly intersect (not just touch at endpoints).
 */
function segmentsIntersect(
  p1: Coordinate,
  p2: Coordinate,
  p3: Coordinate,
  p4: Coordinate
): boolean {
  const d1 = direction(p3, p4, p1);
  const d2 = direction(p3, p4, p2);
  const d3 = direction(p1, p2, p3);
  const d4 = direction(p1, p2, p4);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  // Check collinear cases
  if (d1 === 0 && onSegment(p3, p4, p1)) return true;
  if (d2 === 0 && onSegment(p3, p4, p2)) return true;
  if (d3 === 0 && onSegment(p1, p2, p3)) return true;
  if (d4 === 0 && onSegment(p1, p2, p4)) return true;

  return false;
}

/**
 * Computes the cross product direction of vectors (pi-pk) x (pj-pk).
 */
function direction(pi: Coordinate, pj: Coordinate, pk: Coordinate): number {
  return (pk[0] - pi[0]) * (pj[1] - pi[1]) - (pj[0] - pi[0]) * (pk[1] - pi[1]);
}

/**
 * Checks if point pk lies on segment pi-pj (assuming collinearity).
 */
function onSegment(pi: Coordinate, pj: Coordinate, pk: Coordinate): boolean {
  return (
    Math.min(pi[0], pj[0]) <= pk[0] &&
    pk[0] <= Math.max(pi[0], pj[0]) &&
    Math.min(pi[1], pj[1]) <= pk[1] &&
    pk[1] <= Math.max(pi[1], pj[1])
  );
}

/**
 * Checks if two coordinates are the same point (within floating point tolerance).
 */
function coordsEqual(a: Coordinate, b: Coordinate, epsilon = 1e-10): boolean {
  return Math.abs(a[0] - b[0]) < epsilon && Math.abs(a[1] - b[1]) < epsilon;
}

// ---------------------------------------------------------------------------
// Self-intersection detection
// ---------------------------------------------------------------------------

/**
 * Detects self-intersections in a ring (closed coordinate array).
 * Skips adjacent segments and segments sharing endpoints.
 */
function ringHasSelfIntersection(ring: Coordinate[]): boolean {
  const n = ring.length;
  // A ring needs at least 4 points (3 unique + closing) to possibly self-intersect
  if (n < 5) return false;

  // Check all non-adjacent segment pairs
  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 2; j < n - 1; j++) {
      // Skip the pair where the last segment connects back to the first
      // (they share an endpoint by definition in a closed ring)
      if (i === 0 && j === n - 2) continue;

      const p1 = ring[i];
      const p2 = ring[i + 1];
      const p3 = ring[j];
      const p4 = ring[j + 1];

      // Skip if segments share an endpoint
      if (
        coordsEqual(p1, p3) ||
        coordsEqual(p1, p4) ||
        coordsEqual(p2, p3) ||
        coordsEqual(p2, p4)
      ) {
        continue;
      }

      if (segmentsIntersect(p1, p2, p3, p4)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Detects self-intersections in a linestring (open coordinate array).
 * Skips adjacent segments.
 */
function lineHasSelfIntersection(coords: Coordinate[]): boolean {
  const n = coords.length;
  if (n < 4) return false;

  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 2; j < n - 1; j++) {
      const p1 = coords[i];
      const p2 = coords[i + 1];
      const p3 = coords[j];
      const p4 = coords[j + 1];

      // Skip if segments share an endpoint
      if (
        coordsEqual(p1, p3) ||
        coordsEqual(p1, p4) ||
        coordsEqual(p2, p3) ||
        coordsEqual(p2, p4)
      ) {
        continue;
      }

      if (segmentsIntersect(p1, p2, p3, p4)) {
        return true;
      }
    }
  }

  return false;
}

// ---------------------------------------------------------------------------
// Ring validation
// ---------------------------------------------------------------------------

/**
 * Checks if a ring is properly closed (first coordinate equals last coordinate).
 */
function isRingClosed(ring: Coordinate[], epsilon = 1e-10): boolean {
  if (ring.length < 2) return false;
  return coordsEqual(ring[0], ring[ring.length - 1], epsilon);
}

// ---------------------------------------------------------------------------
// Main validation function
// ---------------------------------------------------------------------------

/**
 * Validates a GeoJSON geometry for topological correctness.
 *
 * Checks performed per geometry type:
 * - **Point**: Always valid
 * - **MultiPoint**: Always valid
 * - **LineString**: Minimum 2 points, no self-intersections
 * - **MultiLineString**: Each line must have minimum 2 points, no self-intersections
 * - **Polygon**: Minimum 4 points per ring (3 unique + closing), ring closure,
 *   no self-intersections
 * - **MultiPolygon**: Each polygon validated individually
 *
 * @param geometry - The GeoJSON geometry to validate
 * @returns ValidationResult with valid flag and array of error messages
 */
export function validateGeometry(geometry: ValidatableGeometry): ValidationResult {
  if (!geometry || !geometry.type) {
    return { valid: false, errors: ['Geometry is null or missing type'] };
  }

  switch (geometry.type) {
    case 'Point':
      return validatePoint(geometry.coordinates);

    case 'MultiPoint':
      return validateMultiPoint(geometry.coordinates);

    case 'LineString':
      return validateLineString(geometry.coordinates as Coordinate[]);

    case 'MultiLineString':
      return validateMultiLineString(geometry.coordinates as Coordinate[][]);

    case 'Polygon':
      return validatePolygon(geometry.coordinates as Coordinate[][]);

    case 'MultiPolygon':
      return validateMultiPolygon(geometry.coordinates as Coordinate[][][]);

    default:
      return { valid: false, errors: [`Unsupported geometry type: ${geometry.type}`] };
  }
}

/**
 * Validates a Point geometry.
 * Points are always valid if they have coordinates.
 */
function validatePoint(coordinates: unknown): ValidationResult {
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return { valid: false, errors: ['Point must have at least 2 coordinate values (x, y)'] };
  }
  if (typeof coordinates[0] !== 'number' || typeof coordinates[1] !== 'number') {
    return { valid: false, errors: ['Point coordinates must be numbers'] };
  }
  return { valid: true, errors: [] };
}

/**
 * Validates a MultiPoint geometry.
 * MultiPoints are always valid if they have at least one point.
 */
function validateMultiPoint(coordinates: unknown): ValidationResult {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return { valid: false, errors: ['MultiPoint must have at least one point'] };
  }
  return { valid: true, errors: [] };
}

/**
 * Validates a LineString geometry.
 * Requires at least 2 points and no self-intersections.
 */
function validateLineString(coordinates: Coordinate[]): ValidationResult {
  const errors: string[] = [];

  if (!Array.isArray(coordinates)) {
    return { valid: false, errors: ['LineString coordinates must be an array'] };
  }

  if (coordinates.length < 2) {
    errors.push('LineString must have at least 2 points');
  }

  if (coordinates.length >= 4 && lineHasSelfIntersection(coordinates)) {
    errors.push('LineString has self-intersection');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validates a MultiLineString geometry.
 * Each line must be a valid LineString.
 */
function validateMultiLineString(coordinates: Coordinate[][]): ValidationResult {
  const errors: string[] = [];

  if (!Array.isArray(coordinates)) {
    return { valid: false, errors: ['MultiLineString coordinates must be an array'] };
  }

  for (let i = 0; i < coordinates.length; i++) {
    const lineResult = validateLineString(coordinates[i]);
    if (!lineResult.valid) {
      errors.push(...lineResult.errors.map((e) => `Line ${i}: ${e}`));
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validates a Polygon geometry.
 * Checks ring closure, minimum vertices, and self-intersections.
 */
function validatePolygon(coordinates: Coordinate[][]): ValidationResult {
  const errors: string[] = [];

  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return { valid: false, errors: ['Polygon must have at least one ring'] };
  }

  for (let i = 0; i < coordinates.length; i++) {
    const ring = coordinates[i];
    const ringLabel = i === 0 ? 'Exterior ring' : `Interior ring ${i}`;

    if (!Array.isArray(ring)) {
      errors.push(`${ringLabel}: must be an array of coordinates`);
      continue;
    }

    // Minimum vertices: 4 points (3 unique + closing coordinate)
    if (ring.length < 4) {
      errors.push(`${ringLabel}: must have at least 4 coordinates (3 unique vertices + closing)`);
      continue;
    }

    // Ring closure check
    if (!isRingClosed(ring)) {
      errors.push(`${ringLabel}: ring is not closed (first and last coordinates must match)`);
    }

    // Self-intersection check
    if (ringHasSelfIntersection(ring)) {
      errors.push(`${ringLabel}: ring has self-intersection`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validates a MultiPolygon geometry.
 * Each polygon must be individually valid.
 */
function validateMultiPolygon(coordinates: Coordinate[][][]): ValidationResult {
  const errors: string[] = [];

  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return { valid: false, errors: ['MultiPolygon must have at least one polygon'] };
  }

  for (let i = 0; i < coordinates.length; i++) {
    const polyResult = validatePolygon(coordinates[i]);
    if (!polyResult.valid) {
      errors.push(...polyResult.errors.map((e) => `Polygon ${i}: ${e}`));
    }
  }

  return { valid: errors.length === 0, errors };
}

export default validateGeometry;
